<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">MARKET RENT WORKSHEET > BASE RENT TAB</h1>
    <span class="banner py-3 mb-4">
      <div>This tab is going to assist you in calculating your Base Rent changes for the budget year.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Top Row Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>The options at the top of the Base Rent tab allow you to add columns for Last Month (Prior Year) and Totals (Prior Year) in addition to YearOverYear Variance Dollar and YearOverYear Variance Percent columns.</div>
          <ul>
            <li><strong>Last Month (Prior Year)</strong> - will show monthly actual numbers for the month prior to the budget start month.</li>
            <li class="no-bullet">
              <ul>
                <li>
                  These numbers can be found by running a Market Rent Schedule Detail report for that prior month and looking at the total of the Base Mkt. Rent column.
                </li>
              </ul>
            </li>
            <li><strong>Totals (Prior Year)</strong> - will show actual 12-month (annual) numbers for the year prior to the budget</li>
            <li class="no-bullet">
              <ul>
                <li>
                  These numbers can be found by running a Market Rent Schedule Detail report for all 12 Month's and adding up the totals for the Base Mkt. Rent columns.
                </li>
              </ul>
            </li>
          </ul>
          <div><strong>YearOverYear Column Definitions:</strong></div>
          <ul>
            <li><strong>YoY Variance $</strong> - Total for this 12 months - total for prior 12 months (as a dollar amount)</li>
            <li><strong>YoY Variance %</strong> - Total for this 12 months vs total for prior 12 months (as a percent)</li>
          </ul>
          <div><strong>Clear and Reset</strong> will clear out all data that has been entered on this tab and it will pull fresh data from the Unit and Unit Type screens for the Base Rent amounts. This will only affect the Base Rent tab.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculation Table</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <ul>
            <li><strong>Monthly Base Rent Change %</strong> - This row is initially blank and allows you to enter a percentage for a specific month that will be applied to all Unit Types and specific Units.</li>
            <li>The <strong>Unit Types</strong> will initially display the Base Rent at the end of the prior year from the Unit Types or Units within ResMan. These numbers can be verified by running a Gross Potential Rent report for the last month of the prior year.</li>
            <li>As Percentages are added above, the Base Rents below at the Unit Type and Unit level will increase by that percentage. You will be able to override these Unit Type or Unit amounts if desired.</li>
            <li>The <strong>Units</strong> will initially display the Base Rent from the Unit Type. These numbers can be verified by running a Gross Potential Rent report for the last month of the prior year. You will be able to override these amounts if desired.</li>
            <li>If you override any Unit amounts, the Unit Type field above will become greyed out and will display an average symbol and the averaged amount of the specific units. If the units are changed so that they all are the same number, that amount will display as the Unit Type number.</li>
          </ul>
          <div>As you complete your budget year Base Rent fields, they will populate in the top summary table.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Right-Side Icons & Ellipsis Menu Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Within each of the Market Rent tabs you will have additional options on the right-side of the screen.</div>
          <img src="../../assets/help/a.png" />
          <div>
            <img class="small" src="../../assets/help/b.png" />
            <span>This icon will bring up a help screen specific to this page</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/c.png" />
            <span>This icon will open the Comments and Flags screen which will allow you to add a general comment, create a flagged comment, or reply to an existing comment. If it is empty in the middle, there are no comments or flags.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/d.png" />
            <span>If it has changed to a solid blue, then comments have been entered.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/e.png" />
            <span>If it has changed to an orange flag, then an unresolved flag or issue exists. Someone will need to click into the Comments and Flags screen and select Resolve the outstanding issue. Once resolved, the icon will change back to blue.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/f.png" />
            <span>Clicking this ellipsis will bring up a new menu of options.</span>
          </div>
          <br />
          <div><strong>Add Flag or Comment</strong> will bring up the same screen as the center icon allowing you to add a comment, a flagged comment or resolve a comment</div>
          <div><strong>Clear & Reset</strong> will clear out any data that has been added and reset this tab back to the default values when the page was first loaded. This only applies to the specific tab you are on. It will also popup a message to explain what is about to happen and to allow you to confirm or cancel.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Market Rent Base Rent Tab')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'MarketRentSummaryHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
